import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info' | 'loading';
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType>({
  toasts: [],
  addToast: () => {},
  removeToast: () => {},
});

let toastCounter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    toastCounter += 1;
    const id = `toast-${toastCounter}-${Date.now()}`;
    const duration = toast.duration ?? 4000;
    setToasts((prev) => [...prev, { ...toast, id }]);
    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-sm w-full pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`pointer-events-auto animate-slide-right flex items-start gap-3 p-4 rounded-xl border shadow-none ${
              toast.type === 'success'
                ? 'bg-green-50 border-green-200 text-green-800'
                : toast.type === 'error'
                ? 'bg-red-50 border-red-200 text-red-800'
                : toast.type === 'warning'
                ? 'bg-amber-50 border-amber-200 text-amber-800'
                : toast.type === 'loading'
                ? 'bg-background-50 border-background-200 text-foreground-800'
                : 'bg-background-50 border-background-200 text-foreground-800'
            }`}
          >
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
              {toast.type === 'success' && <i className="ri-checkbox-circle-fill text-green-500 text-lg"></i>}
              {toast.type === 'error' && <i className="ri-close-circle-fill text-red-500 text-lg"></i>}
              {toast.type === 'warning' && <i className="ri-error-warning-fill text-amber-500 text-lg"></i>}
              {toast.type === 'loading' && <i className="ri-loader-4-line text-primary-500 text-lg animate-spin"></i>}
              {toast.type === 'info' && <i className="ri-information-fill text-primary-500 text-lg"></i>}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">{toast.title}</p>
              {toast.message && <p className="text-xs mt-0.5 opacity-80">{toast.message}</p>}
            </div>
            <button
              onClick={() => removeToast(toast.id)}
              className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity cursor-pointer"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}